import { useEffect, useId, useRef, useState } from "react";
import type { RefObject } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { LAYOUT_NAME, isGroup, navFor } from "../nav";
import type { NavLink as NavLinkItem } from "../nav";
import type { NavGroup, NavItem } from "../nav";
import { useAuthActions, useSession } from "../session";
import { usePreferences } from "@strat/ui";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { FeedbackDialog } from "../components/FeedbackDialog";
import { Icon } from "../components/Icon";
import { Avatar } from "./Avatar";
import { Wordmark } from "./Brand";

/** Close on outside click and Escape. */
function useDismiss<T extends HTMLElement>(open: boolean, close: () => void): RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

function Group({ group }: { group: NavGroup }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));
  const panelId = useId();
  useEffect(() => setOpen(false), [pathname]);
  const active = group.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`));
  return (
    <div className="navgroup" ref={ref}>
      <button
        type="button"
        className={`navgroup__trigger${active ? " is-active" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {group.icon && <Icon name={group.icon} />}
        {group.label}
        <span className={`navgroup__caret${open ? " is-open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="navgroup__panel" id={panelId}>
          <p className="navgroup__head">{group.hint}</p>
          {group.children.map((c) => (
            <NavLink key={c.to} to={c.to} end className="navgroup__item">
              <span className="navgroup__label">
                {c.icon && <Icon name={c.icon} />}
                {c.label}
              </span>
              {c.hint && <span className="navgroup__hint">{c.hint}</span>}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, layout } = useSession();
  const { logout, updateMe } = useAuthActions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  if (!user) return null;

  const other = user.role === "pro" ? "starter" : "pro";
  return (
    <div className="level-menu user-menu" ref={ref}>
      <button
        type="button"
        className="level-menu__trigger user-menu__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Avatar seed={user.avatarSeed} size={26} title={user.displayName} />
        <span className={`level-pill level-pill--${layout === "admin" ? "pro" : layout}`}>
          {LAYOUT_NAME[layout]}
        </span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="level-menu__panel">
          <div className="level-menu__head">
            <strong>{user.displayName}</strong>
            <span>@{user.username}</span>
          </div>
          <Link className="level-menu__switch" to="/me">
            <span className="level-menu__action">
              <Icon name="space" /> Dashboard
            </span>
          </Link>
          <Link className="level-menu__switch" to={`/u/${user.username}`}>
            <span className="level-menu__action">
              <Icon name="user" /> Public profile
            </span>
          </Link>
          <Link className="level-menu__switch" to="/settings">
            <span className="level-menu__action">
              <Icon name="settings" /> Settings
            </span>
          </Link>
          {user.canSwitchLayout && (
            <button
              type="button"
              className="level-menu__switch"
              onClick={() => updateMe.mutate({ layout: other })}
            >
              <span className="level-menu__action">
                <Icon name="switch" /> Switch to {LAYOUT_NAME[other]}
              </span>
              <span className="level-menu__blurb">
                {other === "pro"
                  ? "Adds Optimize, Walk-forward, Robustness and Benchmark."
                  : "The short bar: build, test, share."}
              </span>
            </button>
          )}
          <button
            type="button"
            className="level-menu__switch"
            onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/") })}
          >
            <span className="level-menu__action">
              <Icon name="signout" /> Sign out
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/** True when the page is one of an entry's `also` paths, so Dashboard stays lit on its tabs. */
function underAlso(item: { also?: string[] }, pathname: string): boolean {
  return (item.also ?? []).some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function TopLink({ item }: { item: NavLinkItem }) {
  const { pathname } = useLocation();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `topnav__link${isActive || underAlso(item, pathname) ? " active" : ""}`}
    >
      {item.icon && <Icon name={item.icon} />}
      {item.label}
    </NavLink>
  );
}

function renderItem(item: NavItem) {
  return isGroup(item) ? <Group key={item.id} group={item} /> : <TopLink key={item.to} item={item} />;
}

export function Header() {
  const { user, layout } = useSession();
  const { prefs, toggleTheme: toggle } = usePreferences();
  const theme = prefs.theme;
  const { pathname } = useLocation();
  const [sheet, setSheet] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const unread = useQuery({
    queryKey: ["unread", user?.id],
    queryFn: () => api<{ unread: number }>("/hub/notifications/unread").then((r) => r.unread),
    enabled: !!user,
    refetchInterval: 30_000,
  });
  const sheetId = useId();
  useEffect(() => setSheet(false), [pathname]);
  const nav = navFor(layout);

  return (
    <div className="topbar-wrap" data-layout={layout}>
      <header className="topbar">
        <button
          type="button"
          className="topbar__burger"
          aria-label={sheet ? "Close navigation" : "Open navigation"}
          aria-expanded={sheet}
          aria-controls={sheetId}
          onClick={() => setSheet((v) => !v)}
        >
          <span className={`burger${sheet ? " burger--open" : ""}`} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </button>
        <Link to={user ? "/me" : "/"} className="brand" aria-label="StrategyHive home">
          <Wordmark />
        </Link>
        <nav className="topnav" aria-label="Main">
          {nav.map(renderItem)}
        </nav>
        <div className="topbar__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm topbar__feedback"
            onClick={() => setFeedback(true)}
            title="Report a bug, suggest an idea"
          >
            <Icon name="feedback" /> Feedback
          </button>
          {user && (
            <Link
              to="/notifications"
              className="btn btn--ghost btn--icon bell"
              aria-label={`Notifications${unread.data ? `, ${unread.data} unread` : ""}`}
              title="Notifications"
            >
              <Icon name="bell" size={18} />
              {!!unread.data && <span className="bell__count">{unread.data > 99 ? "99+" : unread.data}</span>}
            </Link>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--icon topbar__display"
            onClick={toggle}
            aria-label="Toggle color theme"
            title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
          </button>
          {user ? (
            <UserMenu />
          ) : (
            <>
              <Link to="/signin" className="btn btn--sm">
                Sign in
              </Link>
              <Link to="/signup" className="btn btn--primary btn--sm hide-narrow">
                Create account
              </Link>
            </>
          )}
        </div>
      </header>
      {feedback && <FeedbackDialog onClose={() => setFeedback(false)} />}
      {sheet && (
        <div className="navsheet" id={sheetId}>
          <nav aria-label="Main">
            {nav.map((item) =>
              isGroup(item) ? (
                <section key={item.id} className="navsheet__group">
                  <h3 className="navsheet__head">
                    <span className="navsheet__title">
                      {item.icon && <Icon name={item.icon} />}
                      {item.label}
                    </span>
                    <span>{item.hint}</span>
                  </h3>
                  {item.children.map((c) => (
                    <NavLink key={c.to} to={c.to} end className="navsheet__link">
                      <span className="navsheet__label">
                        {c.icon && <Icon name={c.icon} />}
                        {c.label}
                      </span>
                      {c.hint && <span className="navsheet__hint">{c.hint}</span>}
                    </NavLink>
                  ))}
                </section>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `navsheet__link${isActive || underAlso(item, pathname) ? " active" : ""}`
                  }
                >
                  <span className="navsheet__label">
                    {item.icon && <Icon name={item.icon} />}
                    {item.label}
                  </span>
                </NavLink>
              ),
            )}
          </nav>
          {/* The header's Feedback button, which has no room on a phone. */}
          <section className="navsheet__group">
            <button
              type="button"
              className="navsheet__toggle"
              onClick={() => {
                setSheet(false);
                setFeedback(true);
              }}
            >
              <span className="navsheet__label">
                <Icon name="feedback" /> Send feedback
              </span>
              <span className="navsheet__value">bug · idea · praise</span>
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
